import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PDFDocument } from "pdf-lib";
import "./AcademicPapersPage.css";

const PROFICIENCY_OPTIONS = [
  {
    value: "beginner",
    eyebrow: "Beginner",
    title: "High school clarity",
    description:
      "Explain every concept from first principles in language a strong high school student can follow.",
  },
  {
    value: "intermediate",
    eyebrow: "Intermediate",
    title: "College-level depth",
    description:
      "Assume the fundamentals are known and focus on what is new, important, and technically interesting.",
  },
  {
    value: "advanced",
    eyebrow: "Advanced",
    title: "Post-grad rigor",
    description:
      "Use an academic tone and cover the paper's technical details, methods, assumptions, and implications in depth.",
  },
];

const DURATION_OPTIONS = [
  {
    value: "1",
    label: "1 minute",
    note: "Fast abstract",
  },
  {
    value: "2.5",
    label: "2.5 minutes",
    note: "Balanced explainer",
  },
  {
    value: "5",
    label: "5 minutes",
    note: "Full breakdown",
  },
];

const GALLERY_PAPERS = [
  {
    id: "attention-is-all-you-need",
    title: "Attention Is All You Need",
    year: "2017",
    category: "Transformers",
    image: "/styles/flat-vector.png",
  },
  {
    id: "alphago",
    title: "Mastering the game of Go with deep neural networks and tree search",
    year: "2016",
    category: "AlphaGo",
    image: "/styles/stylized-3d.png",
  },
  {
    id: "bert",
    title: "BERT: Pre-training of Deep Bidirectional Transformers for Language Understanding",
    year: "2018",
    category: "Language Models",
    image: "/styles/cinematic-clean.png",
  },
  {
    id: "diffusion",
    title: "Denoising Diffusion Probabilistic Models",
    year: "2020",
    category: "Generative AI",
    image: "/styles/parallax-2-5d.png",
  },
];

function getProficiencyInstruction(proficiency) {
  if (proficiency === "beginner") {
    return [
      "Audience level: Beginner.",
      "Assume the viewer has only high school level knowledge of the topic.",
      "Explain every fundamental clearly before using it.",
      "You may describe technical details in a more abstract way, but you must still explain all core ideas from the paper clearly and accessibly.",
      "Avoid unexplained jargon.",
    ].join(" ");
  }

  if (proficiency === "advanced") {
    return [
      "Audience level: Advanced.",
      "Assume the viewer is post-grad or PhD level.",
      "Use an academic tone.",
      "Cover the paper's technicalities in great detail, including methods, assumptions, tradeoffs, evidence, and significance.",
      "Do not oversimplify key mechanisms.",
    ].join(" ");
  }

  return [
    "Audience level: Intermediate.",
    "Assume the viewer is college level and already understands the fundamentals.",
    "Use medium technical depth.",
    "Focus on teaching what is new, notable, and technically meaningful in the paper for someone with prior background knowledge.",
  ].join(" ");
}

function buildAcademicPrompt({ proficiency, duration, fileName }) {
  const durationLabel = `${duration} minute${duration === "1" ? "" : "s"}`;

  return [
    "Turn the attached academic paper PDF into a stellar-looking video.",
    "",
    "Hard rules:",
    `- MUST make the final video duration ${durationLabel}.`,
    "- MUST use the attached academic paper PDF as the primary source material.",
    "- MUST keep the paper attached to this submission and ground the entire video in that attached paper.",
    "- MUST explain the paper's problem, context, method, findings, significance, and limitations accurately.",
    "- MUST not skim over concepts or only gesture at them visually; explain them like a strong teacher whose goal is real understanding, not just presentation.",
    `- ${getProficiencyInstruction(proficiency)}`,
    "- MUST make the video almost entirely Remotion based.",
        "- If and only if Latted agent MUST use AI video for a specific beat, use it sparingly and do not use realistic AI video.",
        "- If AI video is absolutely necessary, it must use an original stylized look that fits the topic instead of realistic footage.",
        "- Prefer motion graphics, typography, diagrams, charts, citations, equations, layouts, and editorial visual systems over generated footage.",
    "- The final piece should feel original, premium, and beautifully art directed rather than generic.",
    "- Keep the narration intellectually honest to the paper and do not fabricate claims not supported by the attachment.",
    "",
    "Execution guidance:",
    "- Open with a precise statement of what the paper is trying to solve.",
    "- Build the explanation in a structured sequence so the viewer can follow the logic of the paper.",
    "- Teach each important concept with the intention of making the viewer actually understand it, not just recognize that it exists.",
    "- Use elegant on-screen labels, section cards, visual metaphors, and citations from the paper.",
    "- Make dense sections legible through animated diagrams and Remotion-native composition.",
    "- End with a clear summary of what is new in the paper and why it matters.",
    "",
    fileName
      ? `Attached paper filename: ${fileName}.`
      : "An academic paper PDF is attached to this job.",
  ].join("\n");
}

function AcademicPapersPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [paperFile, setPaperFile] = useState(null);
  const [pageCount, setPageCount] = useState(null);
  const [proficiency, setProficiency] = useState("intermediate");
  const [duration, setDuration] = useState("2.5");
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPaper, setSelectedPaper] = useState(null);

  const composedPrompt = buildAcademicPrompt({
    proficiency,
    duration,
    fileName: paperFile?.name,
  });

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    if (!selectedPaper) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedPaper(null);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedPaper]);

  const validateAndSetFile = async (file) => {
    if (!file) return;

    setError(null);

    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError("Upload a PDF academic paper. Other file types are not supported here.");
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfDoc.getPageCount();

      if (pages > 200) {
        setError(`This PDF has ${pages} pages. The current limit is 200 pages.`);
        return;
      }

      setPaperFile(file);
      setPageCount(pages);
    } catch (validationError) {
      console.error("Failed to validate academic paper PDF:", validationError);
      setError("The PDF could not be read. Try a different file.");
    }
  };

  const handleFileInput = async (event) => {
    const file = event.target.files?.[0];
    await validateAndSetFile(file);
    event.target.value = "";
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    await validateAndSetFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!paperFile) {
      setError("Upload a PDF paper before starting the compose flow.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const attachmentUrl = URL.createObjectURL(paperFile);
    const params = new URLSearchParams();
    params.set("autoprompt", composedPrompt);
    params.set("autosubmit", "true");
    params.append("attachment", attachmentUrl);

    window.setTimeout(() => {
      URL.revokeObjectURL(attachmentUrl);
    }, 120000);

    navigate({
      pathname: "/",
      search: `?${params.toString()}`,
    });
  };

  return (
    <div className="ap-page">
      <div className="ap-noise" aria-hidden="true" />
      <div className="ap-halo ap-halo--one" aria-hidden="true" />
      <div className="ap-halo ap-halo--two" aria-hidden="true" />

      <header className="ap-topbar">
        <Link to="/" className="ap-brand" aria-label="Latted home">
          <img src="/latted_black.png" alt="Latted" className="ap-brand__logo" />
        </Link>
        <div className="ap-topbar__meta">
          <Link to="/" className="ap-powered ap-powered--link">
            Powered by Latted
          </Link>
        </div>
      </header>

      <main className="ap-shell">
        <section className="ap-copy">
          <div className="ap-copy__hero">
            <div className="ap-kicker">Academic papers to video</div>
            <h1 className="ap-title">
              <span className="ap-title__line">Upload a paper.</span>
              <span className="ap-title__line ap-title__line--nowrap">
                Turn it into an explainer video.
              </span>
            </h1>
            <p className="ap-summary">
              Upload a PDF academic paper, choose the viewer level, set the video
              length, and let Latted turn the paper into a clear, polished
              explainer video.
            </p>
          </div>

          <div className="ap-copy__support">
            <div className="ap-process">
              <div className="ap-process__card">
                <span className="ap-process__step">01</span>
                <p>Upload the paper and choose who the explainer is for.</p>
              </div>
              <div className="ap-process__card">
                <span className="ap-process__step">02</span>
                <p>Pick the target length and start the video creation flow.</p>
              </div>
              <div className="ap-process__card">
                <span className="ap-process__step">03</span>
                <p>Close the tab and wait for an email in 10 to 30 minutes.</p>
              </div>
            </div>

            <div className="ap-gallery">
              <div className="ap-gallery__header">
                <div className="ap-gallery__label">Featured examples</div>
                <p>Preview the kinds of paper-to-video explainers this page will showcase.</p>
              </div>
              <div className="ap-gallery__grid">
                {GALLERY_PAPERS.map((paper) => (
                  <button
                    key={paper.id}
                    type="button"
                    className="ap-gallery-card"
                    onClick={() => setSelectedPaper(paper)}
                  >
                    <span className="ap-gallery-card__icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path d="M7 3.75h7l4.25 4.25v12.25H7z" />
                        <path d="M14 3.75V8h4.25" />
                        <path d="M10 12.25h5.5" />
                        <path d="M10 15.5h5.5" />
                      </svg>
                    </span>
                    <div className="ap-gallery-card__content">
                      <strong>{paper.title}</strong>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="ap-panel-wrap">
          <form className="ap-panel" onSubmit={handleSubmit}>
            <div className="ap-panel__header">
              <div>
                <p className="ap-panel__eyebrow">Submit paper</p>
              </div>
              <p className="ap-panel__microcopy">
                PDF only. One paper. Up to 200 pages.
              </p>
            </div>

            <button
              type="button"
              className={`ap-upload ${isDragging ? "ap-upload--dragging" : ""} ${
                paperFile ? "ap-upload--filled" : ""
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDragging(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (event.target === event.currentTarget) {
                  setIsDragging(false);
                }
              }}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="ap-hidden-input"
                onChange={handleFileInput}
              />

              {paperFile ? (
                <div className="ap-upload__filled">
                  <div className="ap-upload__badge">PDF</div>
                  <div className="ap-upload__details">
                    <strong>{paperFile.name}</strong>
                    <span>
                      {pageCount} pages • {(paperFile.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                  <span className="ap-upload__action">Replace paper</span>
                </div>
              ) : (
                <div className="ap-upload__empty">
                  <span className="ap-upload__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M12 16V4" />
                      <path d="M7 9l5-5 5 5" />
                      <path d="M5 20h14" />
                    </svg>
                  </span>
                  <strong>Drop your academic paper here</strong>
                  <span>or click to upload a PDF</span>
                </div>
              )}
            </button>

            <div className="ap-section">
            <div className="ap-section__header">
              <span className="ap-index">1</span>
              <div>
                <h3>Audience proficiency</h3>
                <p>Pick the technical level of the narration.</p>
              </div>
            </div>
            <div className="ap-choice-grid">
                {PROFICIENCY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`ap-choice ${proficiency === option.value ? "ap-choice--active" : ""}`}
                    onClick={() => setProficiency(option.value)}
                    aria-pressed={proficiency === option.value}
                  >
                    <span className="ap-choice__eyebrow">{option.eyebrow}</span>
                    <strong>{option.title}</strong>
                    <span>{option.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="ap-section">
            <div className="ap-section__header">
                <span className="ap-index">2</span>
                <div>
                  <h3>Target duration</h3>
                  <p>Choose how long the explainer should be.</p>
                </div>
              </div>
              <div className="ap-duration-grid">
                {DURATION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`ap-duration ${duration === option.value ? "ap-duration--active" : ""}`}
                    onClick={() => setDuration(option.value)}
                    aria-pressed={duration === option.value}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.note}</span>
                  </button>
                ))}
              </div>
            </div>

            {error ? <div className="ap-error">{error}</div> : null}

            <div className="ap-submit-row">
              <div className="ap-submit__copy">
                Submit the paper, watch the agent start, then close the tab and
                wait for an email in 10 to 30 minutes.
              </div>
              <button
                type="submit"
                className="ap-submit"
                disabled={!paperFile || isSubmitting}
              >
                {isSubmitting ? "Routing to Latted…" : "Submit paper to Latted"}
              </button>
            </div>
          </form>
        </section>
      </main>

      <div className="ap-signoff">Built by Latted&apos;s 🦞</div>

      {selectedPaper ? (
        <button
          type="button"
          className="ap-modal-backdrop"
          onClick={() => setSelectedPaper(null)}
        >
          <div
            className="ap-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ap-modal__media">
              <img
                src={selectedPaper.image}
                alt={selectedPaper.title}
                className="ap-modal__image"
              />
              <div className="ap-modal__play">
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
            <div className="ap-modal__body">
              <span className="ap-modal__eyebrow">
                {selectedPaper.year} • {selectedPaper.category}
              </span>
              <h2>{selectedPaper.title}</h2>
              <p>
                Example video placeholder. We do not have this showcase video yet,
                but this is where the explainer preview will appear.
              </p>
              <button
                type="button"
                className="ap-modal__close"
                onClick={() => setSelectedPaper(null)}
              >
                Close
              </button>
            </div>
          </div>
        </button>
      ) : null}
    </div>
  );
}

export default AcademicPapersPage;
